import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface DashboardMetrics {
  activeJobs: number;
  overdueJobs: number;
  revenueThisPeriod: number;
  outstandingReceivables: number;
  staffUtilization: number;
  totalClients: number;
  jobsCompletedThisPeriod: number;
  pendingInvoices: number;
}

export interface RevenueDataPoint {
  period: string;
  revenue: number;
  collected: number;
}

export interface JobStatusBreakdown {
  status: string;
  label: string;
  count: number;
  color: string;
}

export interface StaffUtilizationSummary {
  userId: string;
  userName: string;
  utilizationPercentage: number;
  activeJobs: number;
}

export interface DashboardData {
  metrics: DashboardMetrics;
  revenueChart: RevenueDataPoint[];
  jobStatusBreakdown: JobStatusBreakdown[];
  staffUtilization: StaffUtilizationSummary[];
}

/** Role that drives the dashboard filter UX. */
export type DashboardRole = 'admin' | 'manager' | 'member';

export interface ScopeTeam {
  id: string;
  name: string;
}

/** A selectable member; teamIds lists which teams they belong to. */
export interface ScopeMember {
  id: string;
  name: string;
  teamIds: string[];
}

export interface DashboardScopeContext {
  role: DashboardRole;
  teams: ScopeTeam[];
  members: ScopeMember[];
  /** The requesting user's own id (used to pin the member dropdown for members). */
  currentUserId: string;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const dashboardKeys = {
  all: ['dashboard'] as const,
  scopeContext: () => [...dashboardKeys.all, 'scope-context'] as const,
  metrics: (teamId?: string, memberId?: string) =>
    [...dashboardKeys.all, 'metrics', teamId ?? 'all', memberId ?? 'all'] as const,
  revenueChart: (teamId?: string, memberId?: string) =>
    [...dashboardKeys.all, 'revenue-chart', teamId ?? 'all', memberId ?? 'all'] as const,
  jobStatus: (teamId?: string, memberId?: string) =>
    [...dashboardKeys.all, 'job-status', teamId ?? 'all', memberId ?? 'all'] as const,
  staffUtilization: (teamId?: string, memberId?: string) =>
    [...dashboardKeys.all, 'staff-utilization', teamId ?? 'all', memberId ?? 'all'] as const,
};

/** Builds the { team_id, member_id } param object, omitting empty values. */
const scopeParams = (teamId?: string, memberId?: string) => {
  const params: Record<string, string> = {};
  if (teamId) params.team_id = teamId;
  if (memberId) params.member_id = memberId;
  return Object.keys(params).length > 0 ? params : undefined;
};

/** Options shared by the dashboard hooks. `enabled` lets callers skip fetching
 * data the user has no permission to see. */
interface DashboardQueryOptions {
  enabled?: boolean;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch dashboard metrics, optionally scoped to a team.
 * Requirements: 12.1
 */
export function useDashboardMetrics(
  teamId?: string,
  memberId?: string,
  options?: DashboardQueryOptions,
) {
  return useQuery({
    queryKey: dashboardKeys.metrics(teamId, memberId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<DashboardMetrics>>(
        '/reports/dashboard/metrics',
        { params: scopeParams(teamId, memberId) },
      );
      return response.data.data;
    },
    enabled: options?.enabled ?? true,
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes per requirement 12.6
  });
}

/**
 * Fetches the role-driven filter context: which teams (and members, for
 * managers/admins) the user can select, and which dropdown UX applies.
 */
export function useDashboardScopeContext(options?: DashboardQueryOptions) {
  return useQuery({
    queryKey: dashboardKeys.scopeContext(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<DashboardScopeContext>>(
        '/reports/dashboard/scope-context',
      );
      return response.data.data;
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fetch revenue chart data for the dashboard, optionally scoped to a team.
 * Requirements: 12.1
 */
export function useRevenueChart(
  teamId?: string,
  memberId?: string,
  options?: DashboardQueryOptions,
) {
  return useQuery({
    queryKey: dashboardKeys.revenueChart(teamId, memberId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<RevenueDataPoint[]>>(
        '/reports/dashboard/revenue-chart',
        { params: scopeParams(teamId, memberId) },
      );
      return response.data.data;
    },
    enabled: options?.enabled ?? true,
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Fetch job status breakdown, optionally scoped to a team.
 * Requirements: 12.1
 */
export function useJobStatusBreakdown(
  teamId?: string,
  memberId?: string,
  options?: DashboardQueryOptions,
) {
  return useQuery({
    queryKey: dashboardKeys.jobStatus(teamId, memberId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<JobStatusBreakdown[]>>(
        '/reports/dashboard/job-status',
        { params: scopeParams(teamId, memberId) },
      );
      return response.data.data;
    },
    enabled: options?.enabled ?? true,
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Fetch staff utilization summary, optionally scoped to a team.
 * Requirements: 12.1
 */
export function useStaffUtilizationSummary(
  teamId?: string,
  memberId?: string,
  options?: DashboardQueryOptions,
) {
  return useQuery({
    queryKey: dashboardKeys.staffUtilization(teamId, memberId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<StaffUtilizationSummary[]>>(
        '/reports/dashboard/staff-utilization',
        { params: scopeParams(teamId, memberId) },
      );
      return response.data.data;
    },
    enabled: options?.enabled ?? true,
    refetchInterval: 5 * 60 * 1000,
  });
}
